"use client";

import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useRegeneratePollingStore } from "@/store/regeneratePollingStore";
import { useInformation } from "@/context/InformationContext";
import { useAuth } from "@/context/AuthContext";
import { actorConceptsApi } from "@/api/actorConcepts";
import { FORM_STATUS } from "@/constants/generalConstants";
import { NOTIFICATION_TYPES } from "@/constants/NotificationConstants";
import { logger } from "@/utils/logger";

/**
 * Runs in ClientLayout. Polls comment status when user left Talks Editor
 * (owner === "layout") so success/error notification still shows.
 * Only runs when user is logged in; stops and clears store on logout.
 */
export default function RegeneratePollingRunner() {
  const { t } = useTranslation("common");
  const { setNotification } = useInformation();
  const { user } = useAuth();
  const { commentId, owner, stopPolling } = useRegeneratePollingStore();

  const pollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isPollingInProgressRef = useRef(false);
  const retryCountRef = useRef(0);
  const userRef = useRef(user);
  userRef.current = user;
  // Use ref for translation function to prevent polling restart on language change.
  // The ref always holds the latest `t` function, but since the ref object itself
  // doesn't change, it won't trigger the useEffect to re-run when language switches.
  const tRef = useRef(t);
  tRef.current = t;

  useEffect(() => {
    // Only run polling when user is logged in, store owner is "layout" (handoff from tab), and we have a commentId
    if (!user || owner !== "layout" || commentId == null) {
      // If we're bailing out because user logged out but store still has handoff state, clear it
      if (!user && (owner === "layout" || commentId != null)) {
        stopPolling();
      }
      return;
    }

    const clearTimeoutRef = () => {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
    };

    const pollStatus = async () => {
      if (!userRef.current) {
        clearTimeoutRef();
        stopPolling();
        return;
      }
      const state = useRegeneratePollingStore.getState();
      if (state.owner !== "layout" || state.commentId == null) {
        clearTimeoutRef();
        return;
      }
      if (isPollingInProgressRef.current) return;

      let shouldContinue = true;
      isPollingInProgressRef.current = true;

      try {
        const response = await actorConceptsApi.getCommentById(state.commentId);
        if (response.success && response.data) {
          const status = response.data.status;
          retryCountRef.current = 0;

          if (
            status !== FORM_STATUS.READY_FOR_PROMPT.value &&
            status !== FORM_STATUS.PROMPT_IN_PROCESS.value
          ) {
            shouldContinue = false;
            stopPolling();

            if (status !== "APPLIED") {
              setNotification({
                type: NOTIFICATION_TYPES.WARNING,
                message: `Status: ${status}${
                  response.data.comment_text
                    ? ` - ${response.data.comment_text}`
                    : ""
                }`,
                isVisible: true,
                onClose: () => {},
              });
            } else {
              const name =
                state.actorName?.trim() ||
                tRef.current("talksEditor.modifyConversation.actorFallback");
              setNotification({
                type: NOTIFICATION_TYPES.SUCCESS,
                message: tRef.current(
                  "talksEditor.modifyConversation.regenerateSuccess",
                  { actorName: name }
                ),
                isVisible: true,
                onClose: () => {},
              });
            }
          }
        }
      } catch (error) {
        logger.error("RegeneratePollingRunner: poll failed", error);
        retryCountRef.current += 1;
        if (retryCountRef.current >= 5) {
          shouldContinue = false;
          stopPolling();
          setNotification({
            type: NOTIFICATION_TYPES.ERROR,
            message: tRef.current("talksEditor.modifyConversation.pollingFailed"),
            isVisible: true,
            onClose: () => {},
          });
        }
      } finally {
        isPollingInProgressRef.current = false;
        const next = useRegeneratePollingStore.getState();
        if (
          shouldContinue &&
          userRef.current &&
          next.owner === "layout" &&
          next.commentId != null &&
          retryCountRef.current < 5
        ) {
          pollingTimeoutRef.current = setTimeout(pollStatus, 5000);
        } else {
          clearTimeoutRef();
        }
      }
    };

    pollingTimeoutRef.current = setTimeout(pollStatus, 5000);

    return () => {
      clearTimeoutRef();
    };
  }, [user, owner, commentId, stopPolling, setNotification]);

  return null;
}
